<template>
  <div>
    <div
      class="text text_lg text_w-600"
      v-if="item.type === classTypes.TITLE"
    >
      {{ item.title }}
    </div>
    <div
      v-else
      class="class-card"
      :class="classCardClasses"
      :id="item.id"
    >
      <UpgradeLabel
        :premium="true"
        v-if="!currentFeature&&!isMobile&&!isAccessForClass"
        :coords="{ top: 21, right: 18 }"
      />
      <div
        class="class-card__face"
        :class="{'disabled':!currentFeature&&!isAccessForClass}"
        :style="featuredImage"
      >
        <template v-if="item.video">
          <video
            v-if="playerType === 'old'"
            :src="item.video"
            :poster="item.featuredImage"
            preload="metadata"
            @play="handleOnVideoPlay"
            @pause="handleOnVideoPause"
            @loadedmetadata="getVideoDuration"
            controls
          />
          <TVideoPlayer
            v-else
            :options="videoOptions"
            @play="toggleExpandState"
            @pause="toggleExpandState"
            @loadedmetadata="handleOnLoadMetadata"
          />
        </template>
      </div>
      <div class="class-card__body">
        <button
          v-if="!buttonHidden"
          class="class-card__button"
          type="button"
          :aria-label="toggleCompletionStateButtonTitle"
          :title="toggleCompletionStateButtonTitle"
          @click="toggleCompletionState"
        >
        </button>
        <div
          class="class-card__title"
          :class="{'upgrade':!currentFeature&&!isAccessForClass, 'pr-4': !buttonHidden}"
        >
          <UpgradeLabel
            :premium="true"
            v-if="!currentFeature&&isMobile&&!isAccessForClass"
            :coords="{ top: 21, right: 18 }"
          />
          {{ item.title }}
        </div>
        <div class="class-card__description">
          {{ item.description }}
        </div>
        <span
          v-if="(isLive || isTask) && !courseIsRunning && !preview"
          class="class-card__warning"
        >
        {{ courseStartDateFormatted }}
      </span>

        <div v-else-if="isTask">
          <button
            v-if="isActionLink"
            @click="createNote"
            class="class-card__action"
          >{{ item.buttonText }}
          </button>
          <a
            v-else
            class="class-card__action"
            :href="item.buttonURL"
            target="_blank"
            rel="noopener"
          >{{ item.buttonText }}</a>
        </div>

        <div v-else-if="isLive && item.location">
          <div class="ftn-form__label">
            <button
              class="ftn-form__input-postfixum"
              type="button"
              @click="copyWorkshopUrl"
            >
              Copy URL
            </button>
            <div
              class="ftn-form__input"
            >{{ itemLocation }}
            </div>
          </div>
        </div>
        <div v-if="isExtra && item.downloadURL">
          <button
            class="class-card__action"
            type="button"
            :data-loading="downloading"
            :disabled="downloading"
            @click="download"
          >
            Download file
          </button>
        </div>
        <div
          v-if="duration"
          class="class-card__duration"
        >
          {{ formattedDuration }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { classTypes } from '@/utils/class-types';
import userMessages from '@/mixins/user-messages';
import { toggleClassCompletionState } from '@/api/school';
import UpgradeLabel from '@/pages/dashboard/upgrade/UpgradeLabel';
import { TVideoPlayer } from '@/components/TVideoPlayer';

export default {
  name: 'LearnSupportClassCard',
  components: { TVideoPlayer, UpgradeLabel },
  mixins: [userMessages],
  props: {
    item: {
      type: Object,
    },
    preview: {
      type: Boolean,
      default: false,
    },
    roundedCard: {
      type: Boolean,
      default: false,
    },
    buttonHidden: {
      type: Boolean,
      default: false,
    },
    playerType: {
      type: String,
      default: 'old',
    },
  },
  data() {
    return {
      duration: null,
      expanded: false,
      videoPaused: true,
      completed: false,
      downloading: false,
      video: null,
      classTypes,
    };
  },
  computed: {
    ...mapGetters({
      userId: 'auth/id',
      courseStartDateFormatted: 'school/user/courseStartDateFormatted',
      courseIsRunning: 'school/user/courseIsRunning',
      features: 'paidTier/featuresForCurrentTier',
      user: 'user/user',
    }),
    videoOptions() {
      return {
        autoplay: false,
        controls: true,
        sources: [
          {
            src: this.item.video,
            type: 'video/mp4',
          },
        ],
        poster: this.item.featuredImage,
        fluid: true,
        aspectRatio: '16:9',
      };
    },
    isMobile() {
      return window.matchMedia('(max-width: 1150px)').matches;
    },
    currentFeature() {
      return this.features?.CREATOR_BUSINESS_SCHOOL_COURSES;
    },
    isAccessForClass() {
      if (!this.user.id) {
        return true;
      }

      return this.item.level <= this.user.tier.level;
    },
    isActionLink() {
      try {
        return new URL(this.item.buttonURL).pathname === '/notes/create';
      } catch (e) {
        return false;
      }
    },
    formattedDuration() {
      const minutes = Math.floor(this.duration / 60);
      const seconds = Math.floor(this.duration % 60);

      return `${minutes}m ${seconds}s`;
    },
    classCardClasses() {
      return {
        'class-card_expanded': !this.videoPaused,
        'class-card_completed': this.item.completed,
        rounded: this.roundedCard,
      };
    },
    isVideo() {
      return this.item.type === classTypes.ON_DEMAND_VIDEO;
    },
    isTask() {
      return this.item.type === classTypes.TASK;
    },
    isExtra() {
      return this.item.type === classTypes.EXTRA_RESOURCE;
    },
    isLive() {
      return this.item.type === classTypes.LIVE_WORKSHOP;
    },
    toggleCompletionStateButtonTitle() {
      return this.item.completed ? 'Make this class incomplete' : 'Complete this class';
    },
    featuredImage() {
      return this.item.featuredImage
        ? { backgroundImage: `url(${this.item.featuredImage})` }
        : null;
    },
    itemLocation() {
      return this.isAccessForClass ? this.item.location : 'https://liveclassexample.cc';
    },
  },
  methods: {
    async toggleCompletionState() {
      if (!this.isAccessForClass) {
        return;
      }
      try {
        const { status } = await toggleClassCompletionState(this.item.id);
        this.item.completed = status === 200;
      } catch (e) {
        this.setErrorMessage(e);
      }
    },
    getVideoDuration(event) {
      this.duration = event.currentTarget.duration;
    },
    handleOnLoadMetadata(metadata) {
      this.duration = metadata.duration;
    },
    copyWorkshopUrl() {
      if (!this.isAccessForClass) {
        return;
      }
      window.navigator.clipboard.writeText(this.item.location);
      this.setSuccessMessage('Copied');
    },
    download() {
      if (!this.isAccessForClass) {
        return;
      }
      this.downloading = true;
      const xhr = new XMLHttpRequest();
      xhr.responseType = 'blob';
      xhr.onload = () => {
        this.downloading = false;
        const anchor = document.createElement('a');
        anchor.setAttribute('href', URL.createObjectURL(xhr.response));
        anchor.setAttribute('download', 'file');
        anchor.click();
      };
      xhr.open('GET', this.item.downloadURL);
      xhr.send();
    },
    toggleExpandState(event) {
      this.videoPaused = event.type === 'pause';
    },
    handleOnVideoPlay(event) {
      this.toggleExpandState(event);
    },
    handleOnVideoPause(event) {
      setTimeout(() => {
        if (event.target.paused) {
          this.videoPaused = true;
        }
      }, 200);
    },
    createNote() {
      if (!this.isAccessForClass) {
        return;
      }
      this.$router.push({ name: 'NoteCreate', query: { classId: this.item.id } });
    },
  },
};
</script>
